<template>
    <div>
      <InventoryHeaderTab />

      <!-- Page Header (screen only) -->
      <div class="page-header no-print">
        <div>
          <h5 class="page-title">Print Stock Adjustment</h5>
          <p class="page-sub">
            <span class="batch-pill">{{ store_adjustment?.batch_code }}</span>
          </p>
        </div>
        <div class="page-actions">
          <router-link :to="$url('/all-stock-adjustment')" class="btn-outline">
            <font-awesome-icon icon="arrow-left" /> Back
          </router-link>
          <PrintButtonHeader
            :title="'Invoice-' + store_adjustment?.batch_code"
            :urlA4="'/view-stock-adjustment/'"
            :url58mm="'/print58mm-stock-Adjustment/'"
            :url80mm="'/print80mm-stock-Adjustment/'"
            :currentUrl="'Print 80mm Stock Adjustment'"
            :data="store_adjustment"
            :type="'Stock Adjustment'"
          />
          <button class="btn-primary-action no-print" @click="printPage('a4')">
            <font-awesome-icon icon="print" /> Print
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="form-card no-print">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 4" :key="n"></div>
        </div>
      </div>

      <!-- Invoice -->
      <div v-else class="invoice-wrap">
        <div class="invoice" id="printMe">

          <!-- Header -->
          <div class="invoice-header">
            <div>
              <p class="invoice-brand-name">Stock Adjustment</p>
              <p class="invoice-brand-sub">Powered by Mapiibs</p>
            </div>
            <div class="invoice-meta">
              <p class="invoice-batch">{{ store_adjustment?.batch_code }}</p>
              <p class="invoice-date">{{ dateTime(store_adjustment?.date) }}, {{ timeConvert(store_adjustment?.time) }}</p>
            </div>
          </div>

          <div class="invoice-divider"></div>

          <!-- Info Row -->
          <div class="invoice-info-grid">
            <div class="invoice-info-item">
              <span class="invoice-info-label">Store</span>
              <span class="invoice-info-value">{{ store_adjustment?.store?.store_name }}</span>
            </div>
            <div class="invoice-info-item">
              <span class="invoice-info-label">Adjustment No.</span>
              <span class="invoice-info-value mono">{{ store_adjustment?.batch_code }}</span>
            </div>
            <div class="invoice-info-item">
              <span class="invoice-info-label">Adjusted By</span>
              <span class="invoice-info-value">{{ store_adjustment?.user?.fullname }}</span>
            </div>
            <div class="invoice-info-item">
              <span class="invoice-info-label">Date & Time</span>
              <span class="invoice-info-value">{{ dateTime(store_adjustment?.date) }}, {{ timeConvert(store_adjustment?.time) }}</span>
            </div>
          </div>

          <div class="invoice-divider invoice-divider--dashed"></div>

          <!-- Items Table -->
          <table class="invoice-table">
            <thead>
              <tr>
                <th>#</th>
                <th class="col-barcode">Barcode</th>
                <th>Product</th>
                <th class="col-uom">Variant / UOM</th>
                <th>Type</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in adjustment_items" :key="item.id">
                <td class="text-muted">{{ index + 1 }}</td>
                <td class="text-muted mono col-barcode">{{ item?.product?.bar_code || '—' }}</td>
                <td class="fw-bold">{{ item?.product?.product_name }}</td>
                <td class="text-muted col-uom">{{ item?.variant?.variant_name ?? item?.product?.unit_of_measurement }}</td>
                <td>
                  <span class="type-tag" :class="item.adjustment_type == 1 ? 'type-tag--inc' : 'type-tag--dec'">
                    {{ item.adjustment_type == 1 ? '▲ Inc' : '▼ Dec' }}
                  </span>
                </td>
                <td class="text-right fw-bold">{{ item?.quantity }}</td>
                <td class="text-right fw-bold" :class="item.adjustment_type == 0 ? 'text-neg' : 'text-pos'">
                  {{ $filters.formatMoney(item?.total_price) }}
                </td>
              </tr>
            </tbody>
          </table>

          <div class="invoice-divider invoice-divider--dashed"></div>

          <!-- Grand Total -->
          <div class="invoice-total-row">
            <span class="invoice-total-label">Grand Total</span>
            <span class="invoice-total-value" :class="parseFloat(totalPrice) < 0 ? 'text-neg' : 'text-pos'">
              {{ $filters.formatMoney(totalPrice) }}
            </span>
          </div>

          <div class="invoice-divider"></div>

          <!-- Footer -->
          <div class="invoice-footer">
            <p class="invoice-footer-note">Ref: <strong>{{ store_adjustment?.batch_code }}</strong></p>
            <p class="invoice-footer-brand">Powered by Mapiibs</p>
          </div>

        </div>
      </div>
    </div>
  </template>

  <script>
  import InventoryHeaderTab from './component/inventoryheadertab.vue';
  import axios from 'axios';
  import { reusableMixin } from '../../../lib/reusableMixin';

  export default {
    components: { InventoryHeaderTab },
    mixins: [reusableMixin],

    data() {
      return {
        store_adjustment: null,
        adjustment_items: [],
        loading:          false,
      };
    },

    computed: {
      totalPrice() {
        return this.adjustment_items
          .reduce((a, b) => a + parseFloat(b.total_price), 0)
          .toFixed(2);
      },
    },

    created() { this.getStockAdjustment(); },

    methods: {
      // Override mixin's printPage: isolate #printMe from the app shell using
      // the visibility trick so only the invoice renders on paper.
      printPage(width) {
        const isNarrow = /^\d+mm$/.test(width) && parseInt(width) <= 80;
        const margin   = isNarrow ? '4mm' : '12mm';

        const style = document.createElement('style');
        style.id    = '__print-receipt-override';
        style.innerHTML = `
          @page { size: ${width} auto; margin: ${margin}; }
          @media print {
            body > * { visibility: hidden !important; }
            #printMe, #printMe * { visibility: visible !important; }
            #printMe {
              position: absolute;
              left: 0; top: 0;
              width: 100%;
              background: white;
              padding: ${isNarrow ? '6px' : '20px'};
              box-sizing: border-box;
            }
            ${isNarrow ? '.col-barcode, .col-uom { display: none !important; }' : ''}
          }
        `;
        document.head.appendChild(style);
        window.print();
        setTimeout(() => document.getElementById('__print-receipt-override')?.remove(), 1000);
      },

      getStockAdjustment() {
        this.loading = true;
        axios.get(`/inventory/store-adjustment/${this.$route.params.id}`)
          .then(response => {
            this.loading          = false;
            this.store_adjustment = response.data.data;
            this.adjustment_items = response.data.data.adjustment_item;
          })
          .catch(() => {
            this.loading = false;
            this.$toast?.error('Something went wrong');
          });
      },
    },
  };
  </script>

  <style scoped>
  /* ── Screen: Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .page-sub     { margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

  /* ── Skeleton ── */
  .skeleton-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .skeleton      { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }

  /* ── Invoice Wrap (screen preview) ── */
  .invoice-wrap { display:flex; justify-content:center; padding:0 0 32px; }
  .invoice {
    background:#fff; border:1px solid #e4eaf2; border-radius:12px;
    padding:36px 44px; width:100%; max-width:720px;
    box-shadow:0 4px 24px rgba(0,0,0,.06);
  }

  /* ── Invoice Header ── */
  .invoice-header    { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:20px; }
  .invoice-brand-name { font-size:17px; font-weight:800; color:#1a2b45; margin:0 0 2px; }
  .invoice-brand-sub  { font-size:12px; color:#7a90b0; margin:0; }
  .invoice-meta       { text-align:right; }
  .invoice-batch      { font-family:monospace; font-size:14px; font-weight:700; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; display:inline-block; margin:0 0 4px; }
  .invoice-date       { font-size:12px; color:#7a90b0; margin:0; }

  /* ── Dividers ── */
  .invoice-divider         { height:1px; background:#e4eaf2; margin:16px 0; }
  .invoice-divider--dashed { border:none; border-top:1px dashed #e4eaf2; background:transparent; }

  /* ── Info Grid ── */
  .invoice-info-grid  { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .invoice-info-item  { display:flex; flex-direction:column; gap:3px; }
  .invoice-info-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .invoice-info-value { font-size:13px; font-weight:700; color:#1a2b45; }
  .mono { font-family:monospace; }

  /* ── Invoice Table ── */
  .invoice-table          { width:100%; border-collapse:collapse; }
  .invoice-table thead tr { border-bottom:2px solid #e4eaf2; }
  .invoice-table thead th { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:7px 8px; text-align:left; }
  .invoice-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .invoice-table tbody tr:last-child { border-bottom:none; }
  .invoice-table tbody td { padding:9px 8px; font-size:13px; vertical-align:middle; color:#1a2b45; }
  .text-right { text-align:right; }
  .text-muted { color:#4a5e78; }
  .fw-bold    { font-weight:700; }
  .text-pos   { color:#16a34a; }
  .text-neg   { color:#e03535; }

  /* ── Type Tag (text-only, no icon — prints cleanly) ── */
  .type-tag      { display:inline-block; border-radius:4px; padding:2px 6px; font-size:11px; font-weight:700; border:1px solid; }
  .type-tag--inc { color:#16a34a; border-color:#bbf7d0; background:#f0fdf4; }
  .type-tag--dec { color:#e03535; border-color:#fac5c5; background:#fff0f0; }

  /* ── Grand Total ── */
  .invoice-total-row   { display:flex; align-items:center; justify-content:flex-end; gap:14px; }
  .invoice-total-label { font-size:13px; font-weight:700; color:#7a90b0; }
  .invoice-total-value { font-size:20px; font-weight:800; color:#1a2b45; }

  /* ── Footer ── */
  .invoice-footer       { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:6px; }
  .invoice-footer-note  { font-size:12px; color:#4a5e78; margin:0; }
  .invoice-footer-brand { font-size:11px; color:#aab8cc; margin:0; }

  /* ── Buttons ── */
  .batch-pill         { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover { background:#003b8e; }
  .btn-outline        { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; }
  .btn-outline:hover  { background:#f4f7fb; color:#1a2b45; }

  /* ── Responsive ── */
  @media(max-width:768px) {
    .invoice            { padding:20px 16px; }
    .invoice-header     { flex-direction:column; }
    .invoice-meta       { text-align:left; }
    .invoice-info-grid  { grid-template-columns:repeat(2,1fr); }
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .btn-outline,.btn-primary-action { width:100%; justify-content:center; }
  }
  @media(max-width:480px) { .invoice-info-grid { grid-template-columns:1fr; } }
  </style>
